import React, { useState, useEffect } from 'react';
import { FiUser, FiBell, FiSliders, FiCloud, FiSave, FiCheck } from 'react-icons/fi';
import { useTenant } from '../components/Layout';

const DEFAULT_SETTINGS = {
  email: 'admin@company.com',
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  driftAlerts: true,
  complianceViolations: true,
  syncNotifications: true,
  governanceAlerts: true,
  securityAlerts: true,
  emailNotifications: true,
  defaultLandingPage: '/',
  theme: 'light',
  autoSyncInterval: 'manual',
  dataRetention: '90',
  dateFormat: 'MM/DD/YYYY',
  timezone: 'UTC',
  defaultRegion: 'eastus',
  sessionTimeout: '30',
  twoFactorEnabled: false,
};

const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: '1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
};

const sectionTitleStyle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#111827',
  margin: 0,
};

const iconBoxStyle = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.1rem',
  color: '#fff',
};

const labelStyle = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '0.35rem',
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: '0.85rem',
  color: '#111827',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'auto',
};

const fieldGroupStyle = {
  marginBottom: '1rem',
};

const btnPrimaryStyle = {
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.5rem 1.25rem',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
};

const successMsgStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  color: '#059669',
  fontSize: '0.85rem',
  fontWeight: 600,
  marginLeft: '0.75rem',
};

const badgeStyle = {
  background: '#f3f4f6',
  color: '#6b7280',
  fontSize: '0.7rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 10,
  marginLeft: '0.5rem',
};

function Toggle({ value, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: value ? '#4f46e5' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        transition: 'background 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 3,
          left: value ? 23 : 3,
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
}

function ToggleRow({ label, value, onChange, disabled, badge }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: '0.85rem', color: '#111827', fontWeight: 500 }}>
        {label}
        {badge && <span style={badgeStyle}>{badge}</span>}
      </span>
      <Toggle value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

function SaveButton({ onClick, saved }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
      <button style={btnPrimaryStyle} onClick={onClick}>
        <FiSave /> Save Changes
      </button>
      {saved && (
        <span style={successMsgStyle}>
          <FiCheck /> Saved successfully
        </span>
      )}
    </div>
  );
}

export default function Settings() {
  const { tenants } = useTenant();
  const authMethod = localStorage.getItem('lz_auth_method') || 'local';
  const isLocalUser = authMethod === 'local';
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem('lz_settings');
      return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : { ...DEFAULT_SETTINGS };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  });

  const [savedSections, setSavedSections] = useState({});
  const [passwordMsg, setPasswordMsg] = useState('');

  useEffect(() => {
    const { currentPassword, newPassword, confirmPassword, ...toSave } = settings;
    localStorage.setItem('lz_settings', JSON.stringify(toSave));
  }, [settings]);

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const saveSection = (section) => {
    setSavedSections(prev => ({ ...prev, [section]: true }));
    setTimeout(() => setSavedSections(prev => ({ ...prev, [section]: false })), 3000);
  };

  const handlePasswordUpdate = () => {
    if (!settings.currentPassword || !settings.newPassword || !settings.confirmPassword) {
      setPasswordMsg('All password fields are required');
      setTimeout(() => setPasswordMsg(''), 3000);
      return;
    }
    if (settings.newPassword !== settings.confirmPassword) {
      setPasswordMsg('Passwords do not match');
      setTimeout(() => setPasswordMsg(''), 3000);
      return;
    }
    update('currentPassword', '');
    update('newPassword', '');
    update('confirmPassword', '');
    setPasswordMsg('Password updated successfully');
    setTimeout(() => setPasswordMsg(''), 3000);
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Settings</h1>
        <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0.25rem 0 0' }}>Manage your platform preferences and configuration</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        {isLocalUser && (
        <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
          <div style={sectionHeaderStyle}>
            <div style={{ ...iconBoxStyle, background: '#4f46e5' }}><FiUser /></div>
            <h3 style={sectionTitleStyle}>Profile & Account</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Username</label>
                <input style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280' }} value={localStorage.getItem('lz_username') || 'admin'} disabled />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Email Address</label>
                <input style={inputStyle} value={settings.email} onChange={e => update('email', e.target.value)} />
              </div>
            </div>
            <div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Current Password</label>
                <input style={inputStyle} type="password" value={settings.currentPassword} onChange={e => update('currentPassword', e.target.value)} />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>New Password</label>
                <input style={inputStyle} type="password" value={settings.newPassword} onChange={e => update('newPassword', e.target.value)} />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Confirm New Password</label>
                <input style={inputStyle} type="password" value={settings.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} />
              </div>
              <button style={btnPrimaryStyle} onClick={handlePasswordUpdate}>Update Password</button>
              {passwordMsg && (
                <span style={{ ...successMsgStyle, color: passwordMsg.includes('successfully') ? '#059669' : '#ef4444', marginLeft: '0.75rem' }}>
                  {passwordMsg}
                </span>
              )}
            </div>
          </div>
          <SaveButton onClick={() => saveSection('profile')} saved={savedSections.profile} />
        </div>
        )}

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ ...iconBoxStyle, background: '#f59e0b' }}><FiBell /></div>
            <h3 style={sectionTitleStyle}>Notification Preferences</h3>
          </div>
          <ToggleRow label="Email Notifications" value={settings.emailNotifications} onChange={v => update('emailNotifications', v)} />
          <div style={{ height: 8 }} />
          <ToggleRow label="Drift Detection Alerts" value={settings.driftAlerts} onChange={v => update('driftAlerts', v)} />
          <ToggleRow label="Compliance Violations" value={settings.complianceViolations} onChange={v => update('complianceViolations', v)} />
          <ToggleRow label="Sync Completion Notifications" value={settings.syncNotifications} onChange={v => update('syncNotifications', v)} />
          <ToggleRow label="Governance Issue Alerts" value={settings.governanceAlerts} onChange={v => update('governanceAlerts', v)} />
          <ToggleRow label="Security Assessment Alerts" value={settings.securityAlerts} onChange={v => update('securityAlerts', v)} />
          <SaveButton onClick={() => saveSection('notifications')} saved={savedSections.notifications} />
        </div>

        <div style={cardStyle}>
          <div style={sectionHeaderStyle}>
            <div style={{ ...iconBoxStyle, background: '#2563eb' }}><FiCloud /></div>
            <h3 style={sectionTitleStyle}>Cloud Integration</h3>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Connected Tenants</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#4f46e5' }}>{tenants.length}</span>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>tenant{tenants.length !== 1 ? 's' : ''} connected</span>
            </div>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>SDK Status</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669' }} />
              <span style={{ fontSize: '0.85rem', color: '#059669', fontWeight: 600 }}>Connected</span>
            </div>
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Default Region</label>
            <select style={selectStyle} value={settings.defaultRegion} onChange={e => update('defaultRegion', e.target.value)}>
              <optgroup label="Azure">
                <option value="eastus">East US</option>
                <option value="westus2">West US 2</option>
                <option value="westeurope">West Europe</option>
                <option value="northeurope">North Europe</option>
                <option value="uksouth">UK South</option>
              </optgroup>
              <optgroup label="AWS">
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              </optgroup>
              <optgroup label="GCP">
                <option value="us-central1">US Central</option>
                <option value="us-east1">US East</option>
                <option value="europe-west1">Europe West</option>
                <option value="asia-southeast1">Asia Southeast</option>
              </optgroup>
            </select>
          </div>
          <SaveButton onClick={() => saveSection('cloud')} saved={savedSections.cloud} />
        </div>

        <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
          <div style={sectionHeaderStyle}>
            <div style={{ ...iconBoxStyle, background: '#7c3aed' }}><FiSliders /></div>
            <h3 style={sectionTitleStyle}>Platform Preferences</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Default Landing Page</label>
              <select style={selectStyle} value={settings.defaultLandingPage} onChange={e => update('defaultLandingPage', e.target.value)}>
                <option value="/">Overview</option>
                <option value="/drift">Drift Monitoring</option>
                <option value="/gov-framework">LZ Assessment</option>
              </select>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Theme</label>
              <select style={selectStyle} value={settings.theme} onChange={e => update('theme', e.target.value)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Auto-Sync Interval</label>
              <select style={selectStyle} value={settings.autoSyncInterval} onChange={e => update('autoSyncInterval', e.target.value)}>
                <option value="manual">Manual Only</option>
                <option value="6h">Every 6 Hours</option>
                <option value="12h">Every 12 Hours</option>
                <option value="daily">Daily</option>
              </select>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Data Retention</label>
              <select style={selectStyle} value={settings.dataRetention} onChange={e => update('dataRetention', e.target.value)}>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
                <option value="365">1 Year</option>
              </select>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Date Format</label>
              <select style={selectStyle} value={settings.dateFormat} onChange={e => update('dateFormat', e.target.value)}>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Timezone</label>
              <select style={selectStyle} value={settings.timezone} onChange={e => update('timezone', e.target.value)}>
                <option value="UTC">UTC</option>
                <option value="local">Browser Local</option>
                <option value="US/Eastern">US/Eastern</option>
                <option value="US/Pacific">US/Pacific</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Berlin">Europe/Berlin</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
              </select>
            </div>
          </div>
          <SaveButton onClick={() => saveSection('platform')} saved={savedSections.platform} />
        </div>

      </div>
    </div>
  );
}